import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { Avatar, AvatarGradientFallback, AvatarImage, Button, Spinner, Text } from "@athena/ui";
import { CompleteImageUploadDocument, CreateImageUploadDocument } from "@/graphql/media.generated";

/**
 * Image upload field. Requests a presigned PUT via `createImageUpload`, uploads the file, reports
 * the upload complete, then hands the parent both identifiers.
 *
 * **It renders its own preview, and that is the point.** This was a bare button that returned two
 * ids and nothing to show — the real URL is minted by the media service and is not known until
 * the record is re-read. Every caller was therefore expected to invent its own preview, and every
 * caller either skipped it or wired it wrong: the presenter dialog held a `editImagePreview` state
 * that was declared, reset twice, and never once set. So you picked a photo and nothing on screen
 * changed, which is indistinguishable from broken.
 *
 * The fix is ownership rather than another callback. The field shows the current image, swaps in a
 * local `blob:` preview the instant a file is chosen, and reports progress and failure itself.
 * A caller cannot forget to do something it no longer does.
 *
 * **Two identifiers, because callers need different ones.** An organization logo is stored as a
 * `key` in a plain `logo_url` column and resolved back through the `imageUrl` query. A presenter
 * photo is stored as an `imageAssetId` foreign key on the presenter row. Neither is derivable from
 * the other on the client, so both are passed.
 */

/** Accepted types, stated to the operator rather than only enforced. */
const ACCEPTED = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
] as const;

/**
 * Upload ceiling. Generous for a headshot or a logo and small enough that a mis-picked original
 * camera file is refused here — with the size named — rather than after a slow upload fails.
 */
const MAX_BYTES = 5 * 1024 * 1024;

const readableSize = (bytes: number): string =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** The one-line reason a file was refused, or null when it is acceptable. */
const rejectionReason = (file: File): string | null => {
    if (!file.type.startsWith("image/")) {
        return `${file.name} is not an image. Choose a PNG, JPEG, WebP, or GIF.`;
    }
    if (!(ACCEPTED as readonly string[]).includes(file.type)) {
        return `${file.type.replace("image/", "").toUpperCase()} is not supported. Choose a PNG, JPEG, WebP, or GIF.`;
    }
    if (file.size > MAX_BYTES) {
        return `That image is ${readableSize(file.size)}. The limit is ${readableSize(MAX_BYTES)}.`;
    }
    return null;
};

interface ImageUploaderProps {
    /**
     * Owning organization, or null/undefined for a platform-wide image such as a presenter photo,
     * which belongs to no organization (ATL-183). Null requires ADMIN/SYSTEM — there is no org
     * admin to ask — and the backend maps it to its own owner scope.
     */
    organizationId?: string | null;
    onUploaded: (key: string, imageId: string) => void;
    /**
     * The image already on the record, so the field opens showing what is there rather than an
     * empty well that implies there is no photo.
     */
    currentUrl?: string | null;
    /** Called when the operator clears the field. Omit to hide the remove action. */
    onRemoved?: () => void;
    /** Circle for a person, square for a logo — a headshot in a square reads as a mistake. */
    shape?: "circle" | "square";
    /** Initials behind an empty circle, so an unset presenter photo still looks deliberate. */
    fallbackText?: string;
    /**
     * Names the control — it is both the button's text and the input's accessible name, so the two
     * cannot disagree (WCAG 2.5.3 wants the visible label inside the accessible name). Defaults to
     * "Upload/Replace image"; pass a noun-specific pair like "Upload logo" for a clearer button.
     */
    label?: string;
    /** One line under the control. Defaults to the accepted types and the size limit. */
    hint?: string;
}

export function ImageUploader({
    organizationId,
    onUploaded,
    currentUrl,
    onRemoved,
    shape = "square",
    fallbackText,
    label,
    hint,
}: ImageUploaderProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [createUpload] = useMutation(CreateImageUploadDocument);
    const [completeUpload] = useMutation(CompleteImageUploadDocument);

    const [
        busy,
        setBusy,
    ] = useState<boolean>(false);
    const [
        error,
        setError,
    ] = useState<string | null>(null);
    const [
        dragging,
        setDragging,
    ] = useState<boolean>(false);
    /** A `blob:` URL for the chosen file — the only image that exists before the service mints one. */
    const [
        preview,
        setPreview,
    ] = useState<string | null>(null);
    const [
        uploaded,
        setUploaded,
    ] = useState<boolean>(false);

    // Revoking on unmount rather than on replace: the <img> may still be painting the old URL for a
    // frame after the swap, and revoking it early flashes a broken image.
    useEffect(() => () => setPreview((url) => (url && URL.revokeObjectURL(url), null)), []);

    const handleFile = useCallback(
        async (file: File) => {
            const rejection = rejectionReason(file);
            if (rejection) {
                setError(rejection);
                return;
            }

            // Shown before the network is touched, so the operator sees their own file immediately
            // and slow uploads no longer look like nothing happening.
            const localPreview = URL.createObjectURL(file);
            setPreview(localPreview);
            setUploaded(false);
            setBusy(true);
            setError(null);

            try {
                const { data } = await createUpload({
                    variables: { input: { organizationId, filename: file.name, contentType: file.type } },
                });
                const init = data?.createImageUpload;
                if (!init) throw new Error("Upload could not be started");

                let res: Response;
                try {
                    res = await fetch(init.uploadUrl, {
                        method: "PUT",
                        headers: { "Content-Type": file.type },
                        body: file,
                    });
                } catch (networkError) {
                    // A rejected `fetch` means no response arrived at all — the request was blocked
                    // or the network failed — and the browser's own text for that is useless to an
                    // operator: Safari says "Load failed", Chrome "Failed to fetch". This surfaced
                    // verbatim once and read like a bug in the field rather than what it was, a
                    // Content-Security-Policy with no S3 origin in `connect-src`. The real message
                    // still goes to Sentry, which is where a frontend error is diagnosed here — and
                    // a blocked request leaves no other trace at all, since it never reaches AWS.
                    Sentry.captureException(networkError, { tags: { feature: "image-upload" } });
                    throw new Error("Could not reach image storage. Check your connection and try again.");
                }
                if (!res.ok) throw new Error(`The image could not be uploaded (${res.status}). Try again.`);

                // The image is not readable until the store is told the bytes landed. Reporting it
                // before `onUploaded` means the caller never receives a key that does not resolve yet.
                await completeUpload({ variables: { imageId: init.imageId } });
                setUploaded(true);
                onUploaded(init.key, init.imageId);
            } catch (e) {
                // The local preview goes with the failure. Leaving it would show an image that is
                // not actually stored, which is worse than showing none.
                URL.revokeObjectURL(localPreview);
                setPreview(null);
                setError((e as Error).message);
            } finally {
                setBusy(false);
            }
        },
        [
            completeUpload,
            createUpload,
            onUploaded,
            organizationId,
        ],
    );

    /**
     * Clears the input before opening the picker, so choosing the same file twice in a row still
     * fires a change — after a failed attempt, retrying the same file must not look like a dead
     * control.
     *
     * On open rather than inside `onChange`: assigning `value` from within the change handler fires
     * change again, which ran every upload twice. The second run overwrote the first's outcome, so a
     * failed upload reported success.
     */
    const choose = () => {
        if (inputRef.current) inputRef.current.value = "";
        inputRef.current?.click();
    };

    const shown = preview ?? currentUrl ?? null;
    const square = shape === "square";
    // Avatar owns its own rounding through this prop; passing a rounded-* class instead loses to
    // its base class and silently renders a logo as a circle.
    const clip = square ? "rounded-md" : "rounded-full";
    /** One name for the control, used as both the button's text and the input's accessible name. */
    const name = label ?? (shown ? "Replace image" : "Upload image");

    return (
        <div className="space-y-2">
            {/*
             * `tabIndex={-1}` because `sr-only` clips the input without removing it from the tab
             * order. Left focusable it is a second control that opens the same picker under a
             * different name than the button — a duplicate tab stop, and the two names disagree.
             * The button is the control; this is the plumbing it drives.
             */}
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED.join(",")}
                aria-label={name}
                tabIndex={-1}
                className="sr-only"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFile(file);
                }}
            />

            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!busy) setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    if (busy) return;
                    const file = e.dataTransfer.files?.[0];
                    if (file) void handleFile(file);
                }}
                className={[
                    "flex items-center gap-4 rounded-lg border border-dashed p-4 transition-colors",
                    dragging ? "border-ring bg-accent/10" : "border-input bg-muted/40",
                ].join(" ")}
            >
                {/*
                 * A preview, not a control. It was briefly a button as well, which gave the field
                 * three ways to open the same picker and made two elements announce the same name.
                 * The button below is the control; drag-and-drop is the shortcut.
                 */}
                <div className={`relative shrink-0 overflow-hidden ${clip}`}>
                    <Avatar square={square} size="xl">
                        {/*
                         * Both children, always — the fallback is not an else branch.
                         * `AvatarGradientFallback` is Radix's `Avatar.Fallback`, which it shows when
                         * there is no image *or* the image fails to load, and rendering it only when
                         * `shown` is empty threw the second case away: a URL that 403s or is blocked
                         * left `AvatarImage` rendering nothing with no fallback behind it, so the
                         * field showed a blank circle. Radix owns the swap; let it.
                         */}
                        {shown && <AvatarImage src={shown} alt="" className={busy ? "opacity-50" : undefined} />}
                        <AvatarGradientFallback>{fallbackText ?? "—"}</AvatarGradientFallback>
                    </Avatar>
                    {busy && (
                        <span className="absolute inset-0 flex items-center justify-center">
                            <Spinner className="text-foreground size-5" />
                        </span>
                    )}
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="w-auto"
                            disabled={busy}
                            onClick={choose}
                        >
                            {busy ? "Uploading…" : name}
                        </Button>
                        {onRemoved && shown && !busy && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="w-auto"
                                onClick={() => {
                                    setPreview(null);
                                    setUploaded(false);
                                    setError(null);
                                    onRemoved();
                                }}
                            >
                                Remove
                            </Button>
                        )}
                    </div>
                    {/* Wraps rather than truncates: a refusal that says "That image is 8.2…" is useless. */}
                    <Text muted className="text-xs break-words">
                        {hint ?? `PNG, JPEG, WebP, or GIF. Up to ${readableSize(MAX_BYTES)}. Or drag one here.`}
                    </Text>
                </div>
            </div>

            {/* One live region for both outcomes, so a screen reader hears the result either way. */}
            <div aria-live="polite" className="space-y-1">
                {error && (
                    <Text className="text-destructive text-xs break-words" role="alert">
                        {error}
                    </Text>
                )}
                {uploaded && !error && (
                    <Text muted className="text-xs">
                        Photo uploaded. Save to attach it.
                    </Text>
                )}
            </div>
        </div>
    );
}
